import {
  Bell,
  Bluetooth,
  Building,
  Bus,
  CalendarCheck,
  CalendarDays,
  Car,
  Cloud,
  ClipboardList,
  Cpu,
  Dumbbell,
  FileText,
  Gift,
  GraduationCap,
  House,
  KeyRound,
  Layers,
  LayoutDashboard,
  Megaphone,
  Nfc,
  Palette,
  QrCode,
  Radio,
  Rocket,
  ScanLine,
  Search,
  Server,
  ShieldCheck,
  Smartphone,
  Ticket,
  Trophy,
  Users,
  Wrench,
  Zap,
} from "lucide-react";

export const CONTACT = {
  phoneDisplay: "+20 111 167 2926",
  phoneHref: "tel:+201111672926",
  whatsapp: "https://wa.me/201111672926",
  email: "info@fronixstudio.com",
  location: "Cairo, Egypt",
  kntoUrl: "https://kntopass.com/",
};

export const NAV = [
  { id: "platform", label: "KNTO Pass" },
  { id: "hardware", label: "Hardware" },
  { id: "industries", label: "Industries" },
  { id: "services", label: "Custom Software" },
  { id: "contact", label: "Contact" },
];

export const STATS = [
  { value: "10+", label: "Years building access control" },
  { value: "11", label: "Credential types supported" },
  { value: "4", label: "Reader models, made in Egypt" },
  { value: "EN/AR", label: "Bilingual apps out of the box" },
];

export const CREDENTIALS = [
  { label: "Dynamic QR", icon: QrCode },
  { label: "Static QR", icon: QrCode },
  { label: "NFC", icon: Nfc },
  { label: "RFID", icon: Radio },
  { label: "Long-range RFID", icon: Radio },
  { label: "Bluetooth BLE", icon: Bluetooth },
  { label: "ANPR plate recognition", icon: Car },
  { label: "Event tickets", icon: Ticket },
  { label: "Booking QR", icon: CalendarCheck },
  { label: "Wiegand", icon: Cpu },
];

export const PLATFORM_MODULES = [
  {
    icon: KeyRound,
    title: "Access Modules",
    desc: "Standard, event and booking access plans with duration or one-use schemes, plus family and dependent passes.",
    span: "lg:col-span-2",
  },
  {
    icon: Users,
    title: "Visitors & Invitations",
    desc: "Pre-registered and walk-in guests with expiry, start dates and check-in rules.",
  },
  {
    icon: CalendarDays,
    title: "Bookings",
    desc: "Slot-based reservations by hour, day, week or month, with limits per period.",
  },
  {
    icon: Ticket,
    title: "Events & Ticketing",
    desc: "Private and public event QR tickets, delivered with branded invitation emails.",
  },
  {
    icon: Zap,
    title: "Live Access Logs",
    desc: "Every check-in streamed in real time, with PDF, CSV and Excel export.",
    span: "lg:col-span-2",
  },
  {
    icon: Wrench,
    title: "Service Requests",
    desc: "Residents and members raise requests with schedules, holidays and live status tracking.",
  },
  {
    icon: LayoutDashboard,
    title: "Multi-tenant Control",
    desc: "Businesses, projects and granular admin roles, with packages and quotas that grow with you.",
    span: "lg:col-span-2",
  },
  {
    icon: Megaphone,
    title: "Announcements",
    desc: "Targeted news, events and ads with attachments and localized content.",
  },
  {
    icon: Bell,
    title: "Smart Notifications",
    desc: "Scheduled and recurring push and email campaigns to chosen audiences.",
  },
  {
    icon: ClipboardList,
    title: "Forms & Custom Fields",
    desc: "Public registration forms that issue access automatically, with bulk CSV invites for large groups.",
    span: "lg:col-span-2",
  },
  {
    icon: Palette,
    title: "White-label Apps",
    desc: "Your brand on native iOS and Android apps, in English and Arabic.",
    span: "sm:col-span-2",
  },
];

export const ECOSYSTEM = [
  {
    icon: Cloud,
    title: "Cloud Platform",
    desc: "Multi-tenant management portal, APIs and real-time services.",
  },
  {
    icon: Server,
    title: "Local Sync Nodes",
    desc: "On-site synchronization keeps every gate current, even at scale.",
  },
  {
    icon: ScanLine,
    title: "Smart Readers",
    desc: "QR, NFC, RFID and BLE readers with GPS, direction and proximity tuning.",
  },
  {
    icon: Smartphone,
    title: "Mobile Apps",
    desc: "Digital credentials, invitations, bookings and notifications in the pocket.",
  },
];

export const HARDWARE = [
  {
    name: "T-PRO",
    desc: "Enterprise-grade smart access controller designed for large-scale deployments.",
    tag: "Enterprise",
  },
  {
    name: "X-PASS",
    desc: "Advanced access management device supporting real-time synchronization.",
    tag: "Real-time",
  },
  {
    name: "K-PASS",
    desc: "Flexible smart access terminal for commercial and residential environments.",
    tag: "Versatile",
  },
  {
    name: "N-PASS",
    desc: "Compact next-generation access device with offline validation capabilities.",
    tag: "Compact",
  },
];

export const HARDWARE_TECH = [
  "QR Codes",
  "NFC",
  "RFID",
  "Bluetooth",
  "Wiegand",
  "SD Card Storage",
  "Cloud Sync",
];

export const INDUSTRIES = [
  { icon: House, title: "Residential Communities", desc: "Residents, family members, visitors and service requests in one app." },
  { icon: CalendarDays, title: "Events & Ticketing", desc: "Public and private QR tickets with live gate check-in." },
  { icon: Dumbbell, title: "Gyms & Fitness", desc: "Memberships, class bookings and instant entry." },
  { icon: Building, title: "Workspaces", desc: "Employee credentials, meeting guests and attendance." },
  { icon: Trophy, title: "Clubs & Sports", desc: "Member access, dependents and facility reservations." },
  { icon: GraduationCap, title: "Education", desc: "Campus access, student ID and attendance tracking." },
  { icon: Bus, title: "Transport", desc: "Seat reservations validated by QR at boarding." },
  { icon: Gift, title: "Loyalty Programs", desc: "Digital stamps and rewards on the same credential." },
];

export const SERVICES = [
  {
    icon: Layers,
    title: "Platform Engineering",
    desc: "Multi-tenant SaaS, APIs and real-time systems built on .NET and PostgreSQL.",
  },
  {
    icon: Smartphone,
    title: "Mobile Apps",
    desc: "Native-feel React Native apps with BLE, NFC and push built in.",
  },
  {
    icon: Cpu,
    title: "Hardware Integration",
    desc: "Readers, controllers and embedded firmware connected to your cloud.",
  },
  {
    icon: ShieldCheck,
    title: "Security by Design",
    desc: "JWT identity, signed credentials and least-privilege roles from day one.",
  },
];

export const PROCESS = [
  { icon: Search, title: "Discover", desc: "Workshops to map your sites, users and access rules." },
  { icon: FileText, title: "Design", desc: "Architecture, UX flows and a clear delivery plan." },
  { icon: Wrench, title: "Build", desc: "Agile sprints with demos and continuous testing." },
  { icon: Rocket, title: "Launch & Support", desc: "On-site rollout, training and SLA-backed support." },
];

export const TECH_STACK = [
  ".NET 8",
  "ASP.NET Core",
  "PostgreSQL",
  "SignalR",
  "React",
  "React Native",
  "TypeScript",
  "Tailwind CSS",
  "Azure",
  "Firebase",
  "Docker",
  "ESP32",
];

export const ORANGE_CASE = {
  features: [
    "Employees book seats by day from their phone",
    "Drivers validate time-limited QR codes at boarding",
    "Routes, suppliers, bus types and census in one dashboard",
    "Live access logs across every route",
  ],
};
